import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { queryKeys } from '@constants/queryKeys.constant';
import { HttpClientError } from '@errors/httpClient.error';
import { clearLocalAuth } from '@helpers/localAuth.helper';
import { currentSessionQueryOptions } from '@services/sessions/queryOptions';

type RequireActiveSessionInput = {
  isAuthenticated: boolean;
  queryClient: QueryClient;
  redirectTo: string;
};

/**
 * Lets a route load only for a Cognito session the API accepts.
 *
 * - no Cognito session: go to the login page;
 * - API 401: go to login only after local Cognito tokens are gone;
 * - API 403 (account not allowed): show the unauthorized page;
 * - anything else (e.g. 503): rethrow to the route error boundary.
 */
export async function requireActiveSession({
  isAuthenticated,
  queryClient,
  redirectTo,
}: RequireActiveSessionInput): Promise<void> {
  if (!isAuthenticated) {
    queryClient.clear();
    throw loginRedirect(redirectTo);
  }

  try {
    await queryClient.fetchQuery(currentSessionQueryOptions());
  } catch (error) {
    const status =
      error instanceof HttpClientError ? error.response?.status : undefined;

    if (status === 401) {
      // /login sends an authenticated visitor straight back here, so leave
      // only once the Cognito tokens are actually gone.
      queryClient.clear();

      if (await clearLocalAuth()) {
        throw loginRedirect(redirectTo);
      }

      throw error;
    }

    if (status === 403) {
      queryClient.removeQueries({ queryKey: queryKeys.sessions.all });
      throw redirect({ replace: true, to: '/unauthorized' });
    }

    throw error;
  }
}

function loginRedirect(redirectTo: string) {
  return redirect({
    replace: true,
    search: { redirect: redirectTo },
    to: '/login',
  });
}
